import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, X, UploadSimple, Funnel, CalendarBlank } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['Open', 'Closed', 'Awarded', 'Lost', 'Cancelled', 'Under Review', 'Submitted'];

const STATUS_COLORS = {
  Open: 'bg-blue-50 text-blue-700',
  Closed: 'bg-stone-100 text-stone-600',
  Awarded: 'bg-green-50 text-green-700',
  Lost: 'bg-orange-50 text-orange-700',
  Cancelled: 'bg-red-50 text-red-600',
  'Under Review': 'bg-amber-50 text-amber-700',
  Submitted: 'bg-purple-50 text-purple-700',
};

export default function TendersPanel() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [dateFilter, setDateFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);

  const fetchTenders = useCallback(async () => {
    setLoading(true);
    try {
      const params = dateFilter ? { end_date: dateFilter } : {};
      const res = await axios.get(`${API}/tenders`, { params });
      setTenders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFilter]);

  useEffect(() => { fetchTenders(); }, [fetchTenders]);

  useEffect(() => {
    axios.get(`${API}/users`).then(res => setUsers(res.data)).catch(() => {});
  }, []);

  const openModal = (tender = null) => {
    setModal(tender ? 'edit' : 'add');
    setForm(tender || {
      bid_number: '', item_type: '', quantity: '', department: '', end_date: '',
      status: 'Open', user: '', our_quoted_price: '', winning_party: '', winning_amount: '', notes: ''
    });
  };

  const closeModal = () => { setModal(null); setForm({}); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        bid_number: form.bid_number || '',
        item_type: form.item_type,
        quantity: parseInt(form.quantity) || 0,
        department: form.department || '',
        end_date: form.end_date || '',
        status: form.status || 'Open',
        user: form.user || '',
        our_quoted_price: parseFloat(form.our_quoted_price) || 0,
        winning_party: form.winning_party || '',
        winning_amount: parseFloat(form.winning_amount) || 0,
        notes: form.notes || '',
      };
      if (form.id) {
        await axios.put(`${API}/tenders/${form.id}`, data);
      } else {
        await axios.post(`${API}/tenders`, data);
      }
      fetchTenders();
      closeModal();
    } catch (e) { alert('Failed to save tender'); console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tender?')) return;
    try {
      await axios.delete(`${API}/tenders/${id}`);
      fetchTenders();
    } catch (e) { alert('Failed to delete'); }
  };

  const handleExcelUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/tenders/upload-excel`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert(`Imported ${res.data.count} tenders from Excel`);
        fetchTenders();
      } catch (err) {
        alert(err.response?.data?.detail || 'Failed to upload Excel');
      } finally { setUploading(false); }
    };
    input.click();
  };

  const clearFilter = () => setDateFilter('');

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl text-stone-900" data-testid="tenders-title">Govt. Tender Monitoring</h1>
          <p className="text-sm text-stone-500 mt-1">{tenders.length} tender{tenders.length !== 1 ? 's' : ''}{dateFilter ? ` due on ${dateFilter}` : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleExcelUpload} disabled={uploading} data-testid="upload-excel-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-stone-700 bg-stone-100 hover:bg-stone-200 transition-all disabled:opacity-50">
            <UploadSimple size={16} /> {uploading ? 'Uploading...' : 'Upload Excel'}
          </button>
          <button onClick={() => openModal()} data-testid="add-tender-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-white bg-[#002FA7] hover:bg-[#002480] transition-all active:scale-[0.97]">
            <Plus size={14} weight="bold" /> Add Tender
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-stone-200 px-3 py-2">
          <CalendarBlank size={16} className="text-stone-400" />
          <input
            type="date"
            data-testid="date-filter"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="text-sm text-stone-700 bg-transparent outline-none"
          />
        </div>
        {dateFilter && (
          <button onClick={clearFilter} data-testid="clear-filter"
            className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1">
            <Funnel size={12} /> Clear Filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200">
        <div className="overflow-x-auto" data-testid="tender-table-scroll">
        {loading ? (
          <p className="py-10 text-center text-stone-400 text-sm">Loading...</p>
        ) : tenders.length === 0 ? (
          <p className="py-10 text-center text-stone-400 text-sm" data-testid="empty-tenders">
            {dateFilter ? `No tenders due on ${dateFilter}` : 'No tenders found. Add one or upload an Excel file.'}
          </p>
        ) : (
          <table className="w-full text-sm text-left" data-testid="tenders-table" style={{ minWidth: '1100px' }}>
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <Th>Bid No.</Th>
                <Th>Item Type</Th>
                <Th>Qty</Th>
                <Th>Department</Th>
                <Th>End Date</Th>
                <Th>Status</Th>
                <Th>User</Th>
                <Th>Our Quote</Th>
                <Th>Winning Party</Th>
                <Th>Winning Amt.</Th>
                <Th align="center">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {tenders.map(t => (
                <tr key={t.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                  <td className="py-3 px-4 text-stone-600 text-xs font-mono">{t.bid_number || '\u2014'}</td>
                  <td className="py-3 px-4 font-semibold text-stone-900" data-testid={`tender-item-${t.id}`}>{t.item_type}</td>
                  <td className="py-3 px-4 text-stone-600">{t.quantity}</td>
                  <td className="py-3 px-4 text-stone-600">{t.department || '\u2014'}</td>
                  <td className="py-3 px-4 text-stone-600 whitespace-nowrap">{t.end_date || '\u2014'}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 ${STATUS_COLORS[t.status] || 'bg-stone-100 text-stone-600'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-stone-600">{t.user || '\u2014'}</td>
                  <td className="py-3 px-4 text-stone-700 font-medium whitespace-nowrap">
                    {t.our_quoted_price ? `Rs. ${t.our_quoted_price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '\u2014'}
                  </td>
                  <td className="py-3 px-4 text-stone-600">{t.winning_party || '\u2014'}</td>
                  <td className="py-3 px-4 text-stone-700 font-medium">
                    {t.winning_amount ? `Rs. ${t.winning_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '\u2014'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openModal(t)} data-testid={`edit-tender-${t.id}`}
                        className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(t.id)} data-testid={`delete-tender-${t.id}`}
                        className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 pt-16 px-4 overflow-y-auto">
          <div className="bg-white border border-stone-200 w-full max-w-lg mb-10 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h3 className="text-base font-bold text-stone-900">
                {modal === 'edit' ? 'Edit Tender' : 'Add Tender'}
              </h3>
              <button onClick={closeModal} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Bid Number" testId="modal-bid-number" value={form.bid_number}
                  onChange={v => setForm({ ...form, bid_number: v })} placeholder="e.g., GEM/2026/B/12345" />
                <InputField label="Item Type" testId="modal-item-type" value={form.item_type}
                  onChange={v => setForm({ ...form, item_type: v })} required placeholder="e.g., UPS 60kVA, Laptop" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <InputField label="Quantity" testId="modal-quantity" type="number" value={form.quantity}
                  onChange={v => setForm({ ...form, quantity: v })} placeholder="0" />
                <InputField label="Department" testId="modal-department" value={form.department}
                  onChange={v => setForm({ ...form, department: v })} placeholder="e.g., Ministry of Defence" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <InputField label="End Date" testId="modal-end-date" type="date" value={form.end_date}
                  onChange={v => setForm({ ...form, end_date: v })} />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Status</label>
                  <select data-testid="modal-status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">User</label>
                  <select data-testid="modal-tender-user" value={form.user || ''} onChange={e => setForm({ ...form, user: e.target.value })}
                    className="w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] outline-none transition-all">
                    <option value="">Select User</option>
                    {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>
              </div>

              <InputField label="Our Quoted Price (Rs.)" testId="modal-our-quoted-price" type="number" step="0.01"
                value={form.our_quoted_price} onChange={v => setForm({ ...form, our_quoted_price: v })} placeholder="0" />

              <div className="grid grid-cols-2 gap-3">
                <InputField label="Winning Party" testId="modal-winning-party" value={form.winning_party}
                  onChange={v => setForm({ ...form, winning_party: v })} placeholder="Party / Company name" />
                <InputField label="Winning Amount (Rs.)" testId="modal-winning-amount" type="number" step="0.01"
                  value={form.winning_amount} onChange={v => setForm({ ...form, winning_amount: v })} placeholder="0" />
              </div>

              <InputField label="Notes" testId="modal-notes" value={form.notes}
                onChange={v => setForm({ ...form, notes: v })} placeholder="Additional notes..." textarea />

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 py-2 text-sm font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 transition-all">Cancel</button>
                <button type="submit" data-testid="modal-submit-tender"
                  className="flex-1 py-2 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all">
                  {modal === 'edit' ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, align }) {
  return <th className={`py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider ${align === 'center' ? 'text-center' : ''}`}>{children}</th>;
}

function InputField({ label, testId, value, onChange, type = 'text', required, placeholder, step, textarea }) {
  const cls = "w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-all";
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={testId} value={value || ''} onChange={e => onChange(e.target.value)} rows="2" className={cls} required={required} placeholder={placeholder} />
      ) : (
        <input data-testid={testId} type={type} step={step} value={value || ''} onChange={e => onChange(e.target.value)} className={cls} required={required} placeholder={placeholder} />
      )}
    </div>
  );
}
